"use client";

import { Core } from "@faire/design-tokens";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Row } from "@faire/web/slate/Layout";
import { TypographyStyles } from "@faire/web/slate/Typography";
import { legacyComponentCls } from "@faire/web/slate/utils/tracking";
import React from "react";
import styled, { css } from "styled-components";

import { Color } from "../../slate/Color";
import { Notification } from "../../slate/icons/foundation/Notification";

import { NotificationBadgeProps } from "./types";

export const NotificationBadge: React.FC<NotificationBadgeProps> = ({
  backgroundColor = Core.surface.subdued,
  content,
  contentColor = Core.text.default,
  notificationDot,
  className,
  "data-test-id": testId,
}) => {
  const { strictLocalize } = useStrictLocalization();

  const notificationDescription: string = strictLocalize({
    id: "J8xC2I",
    defaultMessage: "with notification",
    description: {
      text: "alt text for screen reader, to indicate a component has a notification dot",
    },
  });

  return (
    <NotificationBadgeWrapper
      $backgroundColor={backgroundColor}
      $contentColor={contentColor}
      $isCircular={content < 10}
      data-test-id={testId}
      className={legacyComponentCls(className)}
    >
      {content > 99 ? "99+" : content}
      {notificationDot ? (
        <>
          <VisuallyHidden>{notificationDescription}</VisuallyHidden>
          <NotificationContainer>
            <Notification />
          </NotificationContainer>
        </>
      ) : null}
    </NotificationBadgeWrapper>
  );
};

const NotificationBadgeWrapper = styled(Row).attrs({
  align: "center",
  justify: "center",
})<{
  $backgroundColor: Color;
  $contentColor: Color;
  $isCircular: boolean;
}>`
  ${(props) =>
    TypographyStyles({
      variant: "paragraphSansMedium",
      theme: props.theme,
    })};
  background-color: ${(props) => props.$backgroundColor};
  block-size: 21px;
  border-radius: 100px;
  color: ${(props) => props.$contentColor};
  inline-size: fit-content;
  min-inline-size: 22px;
  white-space: nowrap;
  position: relative;
  ${({ $isCircular }) =>
    $isCircular
      ? css`
          width: 21px;
          height: 21px;
        `
      : "padding-inline: 8px"}
`;

const NotificationContainer = styled.div.attrs({ "aria-hidden": true })`
  position: absolute;
  top: 0;
  right: 0;
`;

const VisuallyHidden = styled.div`
  opacity: 0;
  pointer-events: none;
  position: absolute;
`;
